import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useModalClose } from '../../other/use-modal-close';
import './Dropdown.css';

const BackdropContainer = styled.div`
  position: fixed;
  inset: 0;
  background-color: transparent;
  z-index: 999;
  cursor: pointer;
  pointer-events: none;

  &.visible {
    pointer-events: auto;
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
  z-index: 1000;
`;

const DropdownContainer = styled.div`
  position: fixed;
  z-index: 1001;
  background-color: ${(props) =>
    props.$netflixStyle ? '#212529' : 'var(--first-color)'};
  border-radius: 0.25rem;
  box-shadow: ${(props) =>
    props.$netflixStyle
      ? '0 2px 8px rgba(0, 0, 0, 0.5)'
      : '0 2px 8px rgba(0, 0, 0, 0.3)'};
  min-width: 10rem;
  opacity: 0;
  transform: translateY(-10px);
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
  pointer-events: none;
  padding: ${(props) => (props.$netflixStyle ? '0.5rem 0' : '0.5rem 0')};
  display: block;

  &.visible {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

const NetflixArrow = styled.div`
  position: fixed;
  top: ${(props) => props.$top || '-8px'};
  left: ${(props) => props.$left || '20px'};
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid #212529;
  z-index: 1002;
  pointer-events: none;
`;

const Dropdown = ({
  isOpen,
  onClose,
  trigger,
  children,
  position = 'bottom',
  netflixStyle = false,
}) => {
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const arrowRef = useRef(null);

  useModalClose(onClose, isOpen);

  useEffect(() => {
    if (!isOpen || !containerRef.current || !wrapperRef.current) return;

    const updatePosition = () => {
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const dropdown = containerRef.current;

      if (position === 'bottom') {
        dropdown.style.top = `${wrapperRect.bottom + 8}px`;
        dropdown.style.bottom = 'auto';

        // Verificar se o dropdown vai escapar pela direita
        const dropdownWidth = dropdown.offsetWidth || 200;
        const leftPosition = wrapperRect.left;
        const rightEdge = leftPosition + dropdownWidth;

        if (rightEdge > window.innerWidth - 10) {
          // Alinhar à direita do wrapper com margem
          dropdown.style.right = `${Math.max(
            10,
            window.innerWidth - wrapperRect.right
          )}px`;
          dropdown.style.left = 'auto';
        } else {
          dropdown.style.left = `${leftPosition}px`;
          dropdown.style.right = 'auto';
        }

        if (netflixStyle && arrowRef.current) {
          const arrowLeft = wrapperRect.left + wrapperRect.width / 2 - 8;
          arrowRef.current.style.left = `${arrowLeft}px`;
          arrowRef.current.style.top = `${wrapperRect.bottom}px`;
        }
      } else if (position === 'top') {
        dropdown.style.bottom = `${window.innerHeight - wrapperRect.top + 8}px`;
        dropdown.style.left = `${wrapperRect.left}px`;
        dropdown.style.right = 'auto';
        dropdown.style.top = 'auto';
      } else if (position === 'right') {
        dropdown.style.top = `${wrapperRect.top}px`;
        dropdown.style.left = `${wrapperRect.right + 8}px`;
        dropdown.style.right = 'auto';
        dropdown.style.bottom = 'auto';
      } else if (position === 'left') {
        dropdown.style.top = `${wrapperRect.top}px`;
        dropdown.style.right = `${window.innerWidth - wrapperRect.left + 8}px`;
        dropdown.style.left = 'auto';
        dropdown.style.bottom = 'auto';
      }
    };

    updatePosition();
    const resizeObserver = new ResizeObserver(updatePosition);
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, position, netflixStyle]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        arrowRef.current &&
        !arrowRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    <>
      {isOpen && (
        <BackdropContainer
          className={isOpen ? 'visible' : ''}
          onClick={(e) => {
            if (
              wrapperRef.current &&
              !wrapperRef.current.contains(e.target) &&
              containerRef.current &&
              !containerRef.current.contains(e.target)
            ) {
              onClose();
            }
          }}
        />
      )}
      <DropdownWrapper ref={wrapperRef}>
        {trigger}
        {isOpen && (
          <>
            {netflixStyle && position === 'bottom' && (
              <NetflixArrow ref={arrowRef} className="netflix-arrow" />
            )}
            <DropdownContainer
              ref={containerRef}
              $netflixStyle={netflixStyle}
              data-netflix-style={netflixStyle}
              className={`dropdown-menu position-${position} ${
                isOpen ? 'visible' : ''
              } ${netflixStyle ? 'netflix-style' : ''}`}>
              {children}
            </DropdownContainer>
          </>
        )}
      </DropdownWrapper>
    </>
  );
};

export default Dropdown;
